// appeal-savings.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { AppealSavingsRow } from '../../models/ratings.type';
import { ServerGridCore } from '../../models/BaseServerGrid';
import { RatingsService } from '../../shared/services/ratings/ratings.service';
import { compareDates, dateFilterComparator, formatDMY } from '../../shared/utils/date-helpers';
import { UserService } from '../../shared/services/user.service';
import { Router } from '@angular/router';
import { BookmarksComponent } from "../bookmarks/bookmarks.component";
import { MatSidenavModule } from "@angular/material/sidenav";
import { AppraisalDataService } from "../../shared/services/appraisal-data.service";

@Component({
  selector: 'app-appeal-savings',
  standalone: true,
  imports: [CommonModule, AgGridModule, MatSidenavModule, BookmarksComponent],
  templateUrl: './appeal-savings.component.html',
  styleUrls: ['./appeal-savings.component.scss']
})
export class AppealSavingsComponent extends ServerGridCore<AppealSavingsRow> {
  bookmarkGrid: string = "appealSavings";
  override api!: GridApi;
  override columnDefs: ColDef[] = [
    { field: 'companyName', headerName: 'Company', filter: 'agTextColumnFilter', hide: true, minWidth: 200 },
    { field: 'clientReference', headerName: 'Client Ref', filter: 'agTextColumnFilter' },
    { field: 'addressPrimary', headerName: 'Address', filter: 'agTextColumnFilter', minWidth: 220 },
    { field: 'cityName', headerName: 'City', filter: 'agTextColumnFilter' },
    { field: 'countryName', headerName: 'Country', filter: 'agTextColumnFilter' },
    {
      field: 'liabilityStartDate',
      headerName: 'Liability Start',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'liabilityEndDate',
      headerName: 'Liability End',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'proposalType', headerName: 'Proposal Type', filter: 'agTextColumnFilter' },
    {
      field: 'servedDate', 
      headerName: 'Served Date',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'targetDate', 
      headerName: 'Target Date',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'hearingDate', 
      headerName: 'Hearing Date',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    {
      field: 'noticeEffectiveDate', 
      headerName: 'Notice Date',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'noticeRateableValue', headerName: 'Notice RV', filter: 'agNumberColumnFilter' },
    {
      field: 'settlementDate', 
      headerName: 'Settlement Date',
      sortable: true,
      // filter: 'agDateColumnFilter',
      valueFormatter: p => formatDMY(p.value),
      comparator: compareDates,
      filterParams: {
        comparator: dateFilterComparator,
        browserDatePicker: true,              // show picker
        buttons: ['reset', 'apply'],
        debounceMs: 0
      }
    },
    { field: 'agreedRateableValue', headerName: 'Agreed RV', filter: 'agNumberColumnFilter' },
    { field: 'settlementStatus', headerName: 'Settlement Status', filter: 'agTextColumnFilter' },
    { field: 'allowanceYear2023', headerName: '23/24', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2024', headerName: '24/25', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2025', headerName: '25/26', filter: 'agNumberColumnFilter' },
    { field: 'allowanceYear2026', headerName: '26/27', filter: 'agNumberColumnFilter' },
    { field: 'revaluationYear', headerName: 'Revaluation Period', hide: true,  filter: 'agTextColumnFilter' }
  ];

  excelStyles = [
    { id: 'header', font: { bold: true } },
    { id: 'date', dataType: 'dateTime', numberFormat: { format: 'dd-mm-yyyy' } },
    { id: 'int', numberFormat: { format: '#,##0' } },
    { id: 'number2', numberFormat: { format: '#,##0.00' } }
  ];

  userData: any;
  constructor(private apiSvc: RatingsService, private userService: UserService, public router: Router,
    private appraisalDataService: AppraisalDataService
  ) { super(); }

  async ngOnInit() {
    this.userService.userData$.subscribe((data) => {
      this.userData = data;
      if (!this.userData.roles.includes('Ratings')) {
        this.router.navigate(['/404']);
      }
    });
  }

  onGridReady(e: GridReadyEvent) {
    this.api = e.api;
    this.api.setGridOption('serverSideDatasource', this.makeDatasource());
  }

  // --- Export button handler (Excel with CSV fallback)
  onExport() {
    if (!this.api) return;

    const columnKeys = this.api.getAllDisplayedColumns().map(c => c.getColId());
    const today = new Date();
    const fileName = `appeal_savings_${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}.xlsx`;
    const hasExcel = typeof (this.api as any).exportDataAsExcel === 'function';

    const dateFields = new Set([
      'liabilityStartDate', 'liabilityEndDate', 'servedDate', 'targetDate',
      'hearingDate', 'noticeEffectiveDate', 'settlementDate'
    ]);

    const processCell = (p: ProcessCellForExportParams) => {
      const f = p.column.getColDef().field;
      if (f && dateFields.has(f) && p.value) {
        const d = new Date(p.value);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        }
      }
      return p.value;
    };

    if (hasExcel) {
      (this.api as any).exportDataAsExcel({
        fileName,
        sheetName: 'Appeal Savings',
        columnKeys,
        processCellCallback: processCell,
        excelStyles: this.excelStyles,
        // SSRM note: exports only rows currently loaded in the client.
      });
    } else {
      this.api.exportDataAsCsv({
        fileName: fileName.replace('.xlsx', '.csv'),
        columnKeys,
        processCellCallback: processCell
      });
    }
  }

  override project(): string[] {
    const m: Record<string, string> = {
      id: 'ID', clientReference: 'ClientReference', addressPrimary: 'AddressPrimary', cityName: 'CityName', countryName: 'CountryName',
      liabilityStartDate: 'LiabilityStartDate', liabilityEndDate: 'LiabilityEndDate', proposalType: 'ProposalType', servedDate: 'ServedDate',
      targetDate: 'TargetDate', hearingDate: 'HearingDate', noticeEffectiveDate: 'NoticeEffectiveDate', noticeRateableValue: 'NoticeRateableValue',
      settlementDate: 'SettlementDate', agreedRateableValue: 'AgreedRateableValue', settlementStatus: 'SettlementStatus', proposalID: 'ProposalID',
      revaluationYear: 'RevaluationYear', companyName: 'CompanyName', allowanceYear2023: 'AllowanceYear2023', allowanceYear2024: 'AllowanceYear2024',
      allowanceYear2025: 'AllowanceYear2025', allowanceYear2026: 'AllowanceYear2026'
    };
    const cols = this.api?.getAllDisplayedColumns() || [];
    return cols.map(c => m[c.getColDef().field!] || c.getColDef().field!);
  }

  override query(body: any) { return this.apiSvc.appealSavingsQuery(body); }

  isBookmarksPanelOpen: boolean = false;
  ontoggleBookmarkSidenav() {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
    this.isBookmarksPanelOpen = !this.isBookmarksPanelOpen;
  }
  onStateUpdated(): void {
    let state = this.api.getState();
    this.appraisalDataService.setGridStateData(state);
  }

  loadBookmark() {
    let state = this.appraisalDataService.getGridStateData();
    this.api.setState(state);
  }
}
